import React, { useState, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CSVImport = ({ collectionType, onImport, onClose }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Review, 3: Import

  const requiredFields = ['name', 'condition'];
  const validConditions = ['mint', 'near-mint', 'excellent', 'very-good', 'good', 'fair', 'poor'];

  const handleFileSelect = (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (selectedFile && selectedFile?.type === 'text/csv') {
      setFile(selectedFile);
      parseCSV(selectedFile);
    } else {
      setErrors(['Please select a valid CSV file']);
    }
  };

  const parseCSV = (file) => {
    setLoading(true);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.map(line => line?.trim())?.filter(line => line);
        
        if (lines?.length < 2) {
          setErrors(['CSV file must contain at least a header row and one data row']);
          setLoading(false);
          return;
        }

        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        const data = [];
        const parseErrors = [];

        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          
          if (values?.length !== headers?.length) {
            parseErrors?.push(`Row ${i + 1}: Column count mismatch`);
            continue;
          }

          const item = {};
          headers?.forEach((header, index) => {
            item[header] = values?.[index];
          });

          // Validate required fields
          const missingFields = requiredFields?.filter(field => !item?.[field] || item?.[field] === '');
          if (missingFields?.length > 0) {
            parseErrors?.push(`Row ${i + 1}: Missing required fields: ${missingFields?.join(', ')}`);
            continue;
          }

          // Validate condition
          if (item?.condition && !validConditions?.includes(item?.condition?.toLowerCase())) {
            parseErrors?.push(`Row ${i + 1}: Invalid condition "${item?.condition}". Must be one of: ${validConditions?.join(', ')}`);
            continue;
          }

          // Clean and format data
          const cleanItem = {
            name: item?.name,
            brand: item?.brand || '',
            model: item?.model || '',
            year: item?.year ? parseInt(item?.year) : null,
            condition: item?.condition?.toLowerCase(),
            purchase_price: item?.purchase_price ? parseFloat(item?.purchase_price) : null,
            current_value: item?.current_value ? parseFloat(item?.current_value) : null,
            estimated_value: item?.estimated_value ? parseFloat(item?.estimated_value) : null,
            description: item?.description || '',
            notes: item?.notes || '',
            rarity_score: item?.rarity_score ? parseInt(item?.rarity_score) : null,
            tags: item?.tags ? item?.tags?.split(';')?.map(t => t?.trim())?.filter(t => t) : [],
            is_featured: item?.is_featured === 'true' || item?.is_featured === '1',
            is_for_sale: item?.is_for_sale === 'true' || item?.is_for_sale === '1'
          };

          data?.push(cleanItem);
        }

        setCsvData(data);
        setErrors(parseErrors);
        
        if (data?.length > 0) {
          setStep(2);
        }

      } catch (error) {
        setErrors(['Failed to parse CSV file. Please check the format.']);
      } finally {
        setLoading(false);
      }
    };

    reader?.readAsText(file);
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      await onImport(csvData);
    } catch (error) {
      setErrors([error?.message || 'Failed to import items']);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'name',
      'brand',
      'model', 
      'year',
      'condition',
      'purchase_price',
      'current_value',
      'estimated_value',
      'description',
      'notes',
      'rarity_score',
      'tags',
      'is_featured',
      'is_for_sale'
    ];
    
    const csvContent = [
      headers?.join(','),
      // Example rows
      'Example Item 1,Example Brand,Model X,2020,mint,100.00,150.00,150.00,Item description,Optional notes,8,tag1;tag2,false,false',
      'Example Item 2,Another Brand,Model Y,2019,excellent,200.00,250.00,275.00,Another description,More notes,7,tag3;tag4,true,true'
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `collection_import_template.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const formatCondition = (condition) => {
    return condition?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'N/A';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">
            Import Items from CSV
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="p-6 space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={32} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground mb-2">
                  Upload CSV File
                </h3>
                <p className="text-muted-foreground">
                  Import multiple items at once using a CSV file. Download our template to get started.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={downloadTemplate}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Template
                  </Button>
                </div>

                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="cursor-pointer flex flex-col items-center space-y-3"
                  >
                    <Icon name="FileText" size={48} className="text-muted-foreground" />
                    <div>
                      <p className="text-lg font-medium text-card-foreground">
                        Choose CSV file
                      </p>
                      <p className="text-sm text-muted-foreground">
                        or drag and drop here
                      </p>
                    </div>
                  </label>
                </div>

                {file && (
                  <div className="flex items-center space-x-3 p-3 bg-muted/20 rounded-lg">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <span className="text-card-foreground">{file?.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ({(file?.size / 1024)?.toFixed(1)} KB)
                    </span>
                  </div>
                )}
              </div>

              {/* Format Guidelines */}
              <div className="bg-muted/20 rounded-lg p-4">
                <h4 className="font-semibold text-card-foreground mb-3">CSV Format Guidelines</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <span className="text-primary">•</span>
                    <span><strong>Required fields:</strong> name, condition</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary">•</span>
                    <span><strong>Valid conditions:</strong> mint, near-mint, excellent, very-good, good, fair, poor</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary">•</span>
                    <span><strong>Tags:</strong> Separate multiple tags with semicolons (tag1;tag2;tag3)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary">•</span>
                    <span><strong>Boolean fields:</strong> Use true/false or 1/0 for is_featured and is_for_sale</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary">•</span>
                    <span><strong>Numbers:</strong> Use decimal format for prices (100.50)</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground">
                    Review Import Data
                  </h3>
                  <p className="text-muted-foreground">
                    {csvData?.length} items ready to import
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back
                </Button>
              </div>

              {errors?.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <h4 className="font-semibold text-destructive mb-2">Import Errors</h4>
                  <ul className="space-y-1">
                    {errors?.map((error, index) => (
                      <li key={index} className="text-sm text-destructive">• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {csvData?.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Brand</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Condition</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Current Value</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Year</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {csvData?.slice(0, 10)?.map((item, index) => (
                          <tr key={index} className="hover:bg-muted/20">
                            <td className="px-4 py-3 text-sm text-card-foreground font-medium">
                              {item?.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {item?.brand || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                {formatCondition(item?.condition)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-success font-medium">
                              {item?.current_value ? `$${item?.current_value?.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {item?.year || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {csvData?.length > 10 && (
                    <div className="px-4 py-3 bg-muted/20 text-center text-sm text-muted-foreground">
                      Showing first 10 items of {csvData?.length} total
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">
                {step === 1 ? 'Processing CSV file...' : 'Importing items...'}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          
          {step === 2 && csvData?.length > 0 && (
            <Button
              onClick={handleImport}
              loading={loading}
              disabled={loading || errors?.length > 0}
              iconName="Upload"
              iconPosition="left"
            >
              {loading ? 'Importing...' : `Import ${csvData?.length} Items`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVImport;